import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

import Button from 'components/Button/Button';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import colors from 'theme/Colors.module.scss';
import 'theme/Forms.scss';

import {
  Button as RegularButton,
  Flex,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  FormLabel,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';

import { paramsToObject } from 'helpers/paramHelpers';

import {
  setInvalidPwResetToken,
  clearInvalidPwResetToken,
} from 'modules/app/appSlice';
import {
  useVerifyResetTokenMutation,
  useChangePasswordMutation,
} from 'modules/app/appApi';

const defaults = {
  newPassword: '',
  confirmPassword: '',
};

const schema = Yup.object().shape({
  newPassword: Yup.string().required('New password is required.').trim(),
  confirmPassword: Yup.string()
    .required('Confirm new password is required.')
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match.')
    .trim(),
});

const NewPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = paramsToObject(search);

  const [noToken, setNoToken] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [verifyResetToken] = useVerifyResetTokenMutation();
  const [changePassword] = useChangePasswordMutation();

  // Selectors
  const invalidPwResetToken = useSelector(
    state => state.app.invalidPwResetToken
  );

  const verifyPayload = { token: params?.t };

  useEffect(() => {
    const initialVerifyToken = async () => {
      if (!params.t) {
        setNoToken(true);
        return;
      }

      if (noToken) {
        setNoToken(false);
      }

      try {
        await verifyResetToken(verifyPayload).unwrap();
        if (invalidPwResetToken) {
          dispatch(clearInvalidPwResetToken());
        }
      } catch (error) {
        dispatch(setInvalidPwResetToken(true));
      }
    };

    initialVerifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values, form) => {
    form.setSubmitting(true);
    const { newPassword, confirmPassword } = values;

    const resetPayload = {
      token: params?.t,
      pw: newPassword,
      pw_confirm: confirmPassword,
    };

    if (!noToken) {
      try {
        await verifyResetToken(resetPayload).unwrap();

        await changePassword(resetPayload).unwrap();

        navigate('/login');

        dispatch(
          pushNotificationWithTimeout({
            type: 'success',
            message: 'Reset password successfully',
          })
        );
      } catch (error) {
        const passwordErrors = error.data.error.data.password;
        passwordErrors.forEach(passwordError => {
          dispatch(
            pushNotificationWithTimeout({
              type: 'error',
              message: passwordError,
            })
          );
        });
      }
    }

    form.setSubmitting(false);
  };

  return (
    <>
      <Formik
        initialValues={defaults}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {form => {
          const { isSubmitting } = form;

          return (
            <Form className="authForm">
              <Text as="p" className="title">
                Reset your Password
              </Text>

              <Flex flexDirection="column" marginY="2rem" gap="2rem">
                <Field name="newPassword">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.newPassword && form.touched.newPassword
                      }
                    >
                      <FormLabel>New Password</FormLabel>
                      <InputGroup size="md">
                        <Input
                          {...field}
                          placeholder="New Password*"
                          focusBorderColor={colors.primary600}
                          backgroundColor={colors.white}
                          disabled={isSubmitting}
                          type={showPassword ? 'text' : 'password'}
                          onFocus={() => {
                            invalidPwResetToken &&
                              dispatch(clearInvalidPwResetToken());
                          }}
                        />
                        <InputRightElement width="4.5rem">
                          <RegularButton
                            h="1.75rem"
                            size="sm"
                            onClick={() => {
                              setShowPassword(!showPassword);
                            }}
                          >
                            {showPassword ? 'Hide' : 'Show'}
                          </RegularButton>
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>
                        {form.errors.newPassword}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Field name="confirmPassword">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.confirmPassword &&
                        form.touched.confirmPassword
                      }
                    >
                      <FormLabel>Confirm New Password</FormLabel>
                      <InputGroup size="md">
                        <Input
                          {...field}
                          placeholder="Confirm New Password*"
                          focusBorderColor={colors.primary600}
                          backgroundColor={colors.white}
                          disabled={isSubmitting}
                          type={showPassword ? 'text' : 'password'}
                          onFocus={() => {
                            invalidPwResetToken &&
                              dispatch(clearInvalidPwResetToken());
                          }}
                        />
                        <InputRightElement width="4.5rem">
                          <RegularButton
                            h="1.75rem"
                            size="sm"
                            onClick={() => {
                              setShowPassword(!showPassword);
                            }}
                          >
                            {showPassword ? (
                              <i className="fas fa-eye-slash" />
                            ) : (
                              <i className="fas fa-eye" />
                            )}
                          </RegularButton>
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>
                        {form.errors.confirmPassword}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {noToken && (
                  <Text as="p" textColor={colors.status100}>
                    <strong>Token not found, please use email link</strong>.
                  </Text>
                )}

                {invalidPwResetToken && (
                  <Text as="p" textColor={colors.status100}>
                    <strong>
                      Invalid token,{' '}
                      <Link to="/reset-password">request a new token</Link>
                    </strong>
                    .
                  </Text>
                )}
              </Flex>

              <Button
                type="submit"
                text="Reset my Password"
                textColor={colors.white}
                btnStyle="solid"
                hoverColor={colors.primary500}
                textHoverColor={colors.white}
                disabled={form.isSubmitting || noToken}
              />

              <Text as="p" className="loginOptions">
                <Link to="/login">Log in</Link>.
              </Text>

              <Text as="p" className="footnote">
                An asterisk (*) marks a required field.
              </Text>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default NewPassword;
